/**
 * Demo/Mock Data for AI Production Control Module
 *
 * This file contains sample data for demonstration purposes.
 * It is used when viewing the Demo Factory (DMO-FAB).
 */

import type { Dataset, TrainingJob, Anomaly, ControlAction, OverviewModel, TrainingHistoryPoint } from "./types";

// Demo Factory ID (DMO-FAB)
export const DEMO_FACTORY_ID = "80788149-4fc9-48d3-a0da-8792db694abc";

export const mockDatasets: Dataset[] = [
  {
    id: "1",
    name: "Welding Parameters Q1",
    station: "Welding Station A",
    samples: 45000,
    labeled: 42500,
    status: "ready",
  },
  {
    id: "2",
    name: "Assembly Torque Data",
    station: "Assembly Line 1",
    samples: 28000,
    labeled: 15000,
    status: "labeling",
  },
  {
    id: "3",
    name: "Paint Thickness Readings",
    station: "Paint Booth 2",
    samples: 12000,
    labeled: 0,
    status: "collecting",
  },
];

export const getStationDeployment = (_stationName: string, index: number) => {
  const deployments = [
    {
      modelName: "Weld Quality Predictor",
      version: "v2.3",
      accuracy: 98.2,
      status: "deployed" as const,
      anomalies: 3,
      lastUpdated: "2024-01-15",
    },
    {
      modelName: "Torque Anomaly Detector",
      version: "v1.1",
      accuracy: 94.5,
      status: "deployed" as const,
      anomalies: 0,
      lastUpdated: "2024-01-14",
    },
    {
      modelName: "Surface Defect Classifier",
      version: "v3.0",
      accuracy: 96.8,
      status: "pending" as const,
      anomalies: 0,
      lastUpdated: "2024-01-13",
    },
    null,
  ];
  return deployments[index % deployments.length];
};

export const mockOverviewModels: OverviewModel[] = [
  {
    id: "1",
    name: "Weld Quality Predictor v2.3",
    station: "Welding Station A",
    accuracy: 98.2,
    status: "deployed",
    anomalies: 3,
  },
  {
    id: "2",
    name: "Torque Anomaly Detector v1.1",
    station: "Assembly Line 1",
    accuracy: 94.5,
    status: "deployed",
    anomalies: 0,
  },
  {
    id: "3",
    name: "Surface Defect Classifier v3.0",
    station: "Paint Booth 2",
    accuracy: 96.8,
    status: "deployed",
    anomalies: 0,
  },
];

export const mockControlActions: ControlAction[] = [
  {
    id: "1",
    timestamp: "2024-01-15 14:32:05",
    station: "Welding Station A",
    action: "Adjusted voltage from 24.5V to 25.1V",
    trigger: "Anomaly detected",
    status: "executed",
  },
  {
    id: "2",
    timestamp: "2024-01-15 14:28:12",
    station: "Welding Station A",
    action: "Wire feed rate reduced by 2%",
    trigger: "Quality prediction",
    status: "executed",
  },
  {
    id: "3",
    timestamp: "2024-01-15 14:15:00",
    station: "Assembly Line 1",
    action: "Torque limit adjustment pending",
    trigger: "Manual override",
    status: "pending",
  },
];

export const mockAnomalies: Anomaly[] = [
  {
    id: "1",
    timestamp: "2024-01-15 14:30:00",
    station: "Welding Station A",
    type: "Parameter Drift",
    severity: "medium",
    resolved: false,
  },
  {
    id: "2",
    timestamp: "2024-01-15 14:25:00",
    station: "Welding Station A",
    type: "Quality Risk",
    severity: "low",
    resolved: true,
  },
  {
    id: "3",
    timestamp: "2024-01-15 14:20:00",
    station: "Welding Station A",
    type: "Process Deviation",
    severity: "high",
    resolved: false,
  },
];

export const mockTrainingJobs: TrainingJob[] = [
  {
    id: "1",
    name: "Weld Quality Predictor v2.4",
    dataset: "Welding Parameters Q1",
    station: "Welding Station A",
    status: "running",
    progress: 67,
    epoch: 134,
    totalEpochs: 200,
    loss: 0.0234,
    accuracy: 97.8,
    startedAt: "2024-01-15 12:30:00",
    eta: "~45 min",
  },
  {
    id: "2",
    name: "Torque Anomaly Detector v1.2",
    dataset: "Assembly Torque Data",
    station: "Assembly Line 1",
    status: "queued",
    progress: 0,
    epoch: 0,
    totalEpochs: 150,
    loss: null,
    accuracy: null,
    startedAt: null,
    eta: "Waiting...",
  },
  {
    id: "3",
    name: "Surface Defect Classifier v3.1",
    dataset: "Paint Thickness Readings",
    station: "Paint Booth 2",
    status: "completed",
    progress: 100,
    epoch: 200,
    totalEpochs: 200,
    loss: 0.0156,
    accuracy: 98.4,
    startedAt: "2024-01-14 08:00:00",
    eta: "Completed",
  },
  {
    id: "4",
    name: "Weld Spatter Detection v1.0",
    dataset: "Welding Parameters Q1",
    station: "Welding Station A",
    status: "failed",
    progress: 45,
    epoch: 90,
    totalEpochs: 200,
    loss: 0.892,
    accuracy: 62.3,
    startedAt: "2024-01-13 14:00:00",
    eta: "Failed at epoch 90",
  },
];

export const mockTrainingHistory: TrainingHistoryPoint[] = [
  { epoch: 1, loss: 0.8542, valLoss: 0.8234, accuracy: 52.1 },
  { epoch: 25, loss: 0.4231, valLoss: 0.4567, accuracy: 71.3 },
  { epoch: 50, loss: 0.2134, valLoss: 0.2456, accuracy: 82.5 },
  { epoch: 75, loss: 0.1234, valLoss: 0.1567, accuracy: 89.2 },
  { epoch: 100, loss: 0.0654, valLoss: 0.0823, accuracy: 93.8 },
  { epoch: 125, loss: 0.0387, valLoss: 0.0512, accuracy: 96.1 },
  { epoch: 134, loss: 0.0234, valLoss: 0.0345, accuracy: 97.8 },
];
